import React, { useState } from 'react';
import { BASE_URL } from '../apiConfig';
import { Alert, AlertIcon, AlertTitle } from "./ui/alert";
import { RiCheckboxCircleFill } from "@remixicon/react";
import { Link } from 'react-router-dom';
import ucdpLogo from "../assets/logo3.png";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BASE_URL}/user/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send password reset email');
      }

      setSuccess(true);
    } catch (error: any) {
      setError(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen relative flex overflow-hidden px-4 md:px-15">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[#000000]">
        {/* Radial Gradient Background */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(49.38% 45.34% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%)",
          }}
        />

        {/* Rounded Rectangle */}
        <img
          src="/Rounded rectangle.png"
          alt=""
          className="hidden md:block absolute"
          style={{
            width: "10%",
            height: "auto",
            top: "90%",
            left: "0%",
            opacity: 1,
            pointerEvents: "none",
            userSelect: "none",
          }}
          draggable={false}
        />

        {/* Circle */}
        <img
          src="/Circle.png"
          alt=""
          className="hidden md:block absolute"
          style={{
            width: "8%",
            height: "auto",
            top: "60%",
            right: "0%",
            opacity: 1,
            pointerEvents: "none",
            userSelect: "none",
          }}
          draggable={false}
        />

        {/* Dots Pattern */}
        <div
          className="absolute"
          style={{
            width: "1106px",
            height: "808px",
            top: "-12px",
            left: "194px",
            backgroundImage: "url('/dots.png')",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            opacity: 1,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col w-full">
        {/* Navigation */}
        <nav className="p-4 md:p-6">
          <Link to="/" className="flex items-center">
            <img src={ucdpLogo} alt="UCDP Logo" className="h-16 md:h-20 w-auto drop-shadow-lg" />
          </Link>
        </nav>

        {/* Content Container */}
        <div className="flex flex-col md:flex-row items-center px-0 md:px-6 h-full justify-start md:justify-between gap-y-4">
          {/* Left Side Content (Desktop) */}
          <div className="hidden md:flex w-full md:flex-1 justify-center md:justify-start mb-8 md:mb-0 -mt-20">
            <div className="w-full md:w-[484px] md:h-[185px] flex flex-col items-center text-center space-y-[27px]">
              <h2 className="w-full md:w-[484px] font-ibm-plex-sans !font-bold text-[36px] leading-[120%] tracking-[-0.02em] text-white text-center">
                Reset Your Password
              </h2>
              <p className="w-full md:w-[364px] font-lora !font-normal text-[14px] leading-4 text-[#FFFFFFB2] text-center">
                Don't worry! It happens. Please enter the email address associated with your account.
              </p>
            </div>
          </div>

          {/* Heading & Description (Mobile) */}
          <div className="md:hidden w-full flex flex-col items-center justify-center mt-30 mb-2">
            <h2 className="font-ibm-plex-sans font-bold text-[24px] leading-[120%] tracking-[-0.02em] text-white text-center mb-2">
              Reset Your Password
            </h2>
            <p className="font-lora font-normal text-[14px] leading-4 text-[#FFFFFFB2] text-center max-w-xs">
              Don't worry! It happens. Please enter the email address associated with your account.
            </p>
          </div>

          {/* Right Side Form */}
          <div className="w-full md:w-[440px] flex flex-col items-center justify-center mt-6 md:ml-16 md:-mt-40">
            <div className="w-full max-w-[360px] flex flex-col items-center gap-4 md:gap-8 px-4">
              {success ? (
                <div className="text-center space-y-4 bg-[#20242D] p-6 rounded-[12px] border border-[#363F54]">
                  <Alert variant="success">
                    <AlertIcon>
                      <RiCheckboxCircleFill />
                    </AlertIcon>
                    <AlertTitle>Password reset email sent!</AlertTitle>
                  </Alert>
                  <p className="mt-4 text-gray-300">
                    If an account exists with this email, you will receive a password reset link shortly.
                    Please check your email and follow the instructions.
                  </p>
                  <Link
                    to="/login"
                    className="block mt-4 text-[#FF541F] hover:text-[#ff6a38] transition-colors"
                  >
                    Back to Login
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="w-full space-y-6">
                  {error && (
                    <Alert variant="destructive" close={true} onClose={() => setError('')}>
                      <AlertIcon>
                        <span className="text-xl">!</span>
                      </AlertIcon>
                      <AlertTitle>{error}</AlertTitle>
                    </Alert>
                  )}

                  <div className="flex flex-col gap-2 md:gap-[10px]">
                    <label className="block text-[13px] md:text-sm text-gray-300" htmlFor="email">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-[38px] md:h-10 px-3 md:px-[10px] py-0 bg-[#20242D] border border-[#363F54] rounded-[12px] text-white text-[14px] placeholder-[#93A0B9] focus:outline-none"
                      placeholder="example@gmail.com"
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full h-[38px] md:h-10 py-[6px] px-[12px] rounded-[8px] font-inter font-medium text-[14px] leading-[100%] text-white
                    bg-[linear-gradient(0deg,#FF541F,#FF541F),linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_100%)]
                    shadow-[0px_0px_0px_1px_#1A5CFF99] transition-colors"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send Reset Link"}
                  </button>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="font-inter font-medium text-[12px] md:text-[14px] leading-[20px] !text-[#FF541F] hover:!text-[#ff6a38] transition-colors"
                    >
                      Back to Login
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;